import { useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Modal,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";

import type { GetDraftActivitiesQuery } from "@/graphql/graphql";
import { GET_DRAFT_ACTIVITIES_QUERY } from "@/pages/ActivityLibrary/queries";
import { TEXT_DEBOUNCE_MS } from "@/utils/consts";

import {
  draftActivitesModalInteractiveCellSx,
  draftActivitesModalPaperSx,
  draftActivitesModalTableWrapSx,
} from "./DraftActivitesModal.styles";

type DraftActivitesModalProps = {
  open: boolean;
  onClose: () => void;
  onLoadDraft: (draft: GetDraftActivitiesQuery["draftActivities"][number]) => void;
};

export const DraftActivitesModal: React.FC<DraftActivitesModalProps> = ({
  open,
  onClose,
  onLoadDraft,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [debouncedFilter, setDebouncedFilter] = useState("");
  const lastDebouncedFilterRef = useRef("");

  useEffect(() => {
    const handle = setTimeout(() => {
      const trimmed = searchValue.trim();
      if (trimmed === lastDebouncedFilterRef.current) {
        return;
      }

      lastDebouncedFilterRef.current = trimmed;
      setDebouncedFilter(trimmed);
    }, TEXT_DEBOUNCE_MS);

    return () => clearTimeout(handle);
  }, [searchValue]);

  const { data, loading, error } = useQuery<GetDraftActivitiesQuery>(GET_DRAFT_ACTIVITIES_QUERY, {
    skip: !open,
    variables: { filter: debouncedFilter || undefined },
    fetchPolicy: "cache-and-network",
  });

  const drafts = useMemo(() => data?.draftActivities ?? [], [data?.draftActivities]);

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={draftActivitesModalPaperSx}>
        <Typography variant="h6">Load draft</Typography>
        <TextField
          size="small"
          label="Search drafts"
          value={searchValue}
          onChange={(event) => setSearchValue(event.target.value)}
          placeholder="Search by title or content"
        />

        {error && <Alert severity="error">Could not load drafts.</Alert>}

        {!error && loading && !drafts.length && (
          <Stack alignItems="center" py={3}>
            <CircularProgress size={28} />
          </Stack>
        )}

        {!error && !loading && !drafts.length && (
          <Typography variant="body2" color="text.secondary">
            No drafts found.
          </Typography>
        )}

        {!!drafts.length && (
          <Box sx={draftActivitesModalTableWrapSx}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {drafts.map((draft) => (
                  <TableRow key={draft.id} hover>
                    <TableCell
                      sx={draftActivitesModalInteractiveCellSx}
                      onClick={() => onLoadDraft(draft)}
                    >
                      {draft.title}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={draftActivitesModalInteractiveCellSx}
                      onClick={() => onLoadDraft(draft)}
                    >
                      <Button size="small" variant="outlined" onClick={() => onLoadDraft(draft)}>
                        Load
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        <Stack direction="row" justifyContent="flex-end">
          <Button variant="outlined" onClick={onClose}>
            Close
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
};
